import { useState, useRef } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useAssistantStore } from '@/stores/assistantStore';
import { useAssistantChat } from '@/hooks/useAssistantChat';
import { quoteResultActionSchema } from '@/lib/quoteResultAction';
import {
  quoteEditorPath,
  quoteResultScope,
  revalidateQuoteResult,
} from '@/lib/quoteResultNavigation';
import { sameAssistantPrincipal } from '@/lib/assistantPrincipal';
import { useCurrentQuoteNavigation } from '@/hooks/useCurrentQuoteNavigation';

export function QuoteConversationReturn({ quoteId }: { quoteId: string }) {
  const location = useLocation();
  const { loadHistory } = useAssistantChat();
  const { t } = useTranslation('assistant');
  const { t: tw } = useTranslation('workorders');
  const isCurrent = useCurrentQuoteNavigation();
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [refreshed, setRefreshed] = useState<string | null>(null);
  const state = location.state as { quoteReturn?: { action?: unknown; family?: unknown } } | null;
  const parsed = quoteResultActionSchema.safeParse(state?.quoteReturn?.action);
  const allowed = useAuthStore(
    (auth) =>
      parsed.success &&
      quoteResultScope(parsed.data, auth) !== undefined &&
      state?.quoteReturn?.family === auth.sessionFamilyId
  );
  if (!parsed.success || parsed.data.quoteId !== quoteId) return null;
  const action = parsed.data;
  const returnToConversation = async () => {
    if (inFlight.current || !allowed) return;
    inFlight.current = true;
    setBusy(true);
    setFailed(false);
    setRefreshed(null);
    try {
      const checked = await revalidateQuoteResult(action);
      if (!isCurrent()) return;
      // This explicit return never re-arms a pending confirmation or dispatches an agent turn.
      const loaded = await loadHistory(action.conversationId, { restorePending: false, isCurrent });
      const scope = quoteResultScope(action);
      if (!isCurrent()) return;
      if (
        !loaded ||
        !scope ||
        !sameAssistantPrincipal(checked.scope) ||
        useAssistantStore.getState().conversationId !== action.conversationId
      )
        throw new Error('quote_result_unavailable');
      const store = useAssistantStore.getState();
      store.setCurrentContext({
        route: quoteEditorPath(action),
        entityType: 'workOrder',
        entityId: action.quoteId,
        entityName: checked.current.workOrderNumber ?? action.quoteId,
        entityStatus: checked.current.status,
        entityUpdatedAt: checked.current.updatedAt,
      });
      store.setView('chat');
      store.setOpen(true);
      setRefreshed(checked.current.status);
    } catch {
      if (isCurrent()) setFailed(true);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  // Returning only opens the original chat beside the editor: unsaved forms remain intact.
  return (
    <Box sx={{ mb: 1 }}>
      <Button
        disabled={!allowed || busy}
        onClick={() => {
          void returnToConversation();
        }}
      >
        {t('quoteResult.return')}
      </Button>
      {failed && <Alert severity="warning">{t('quoteResult.unavailable')}</Alert>}
      {refreshed && (
        <Alert severity="info">
          {t('quoteResult.refreshed', {
            status: tw(`status.${refreshed}`, { defaultValue: refreshed }),
          })}
        </Alert>
      )}
    </Box>
  );
}
